// Sound notification manager for ERP system
class SoundManager {
  constructor() {
    this.sounds = {
      success: new Audio('/success.mp3'),
      error: new Audio('/error.mp3'),
      warning: new Audio('/warning.mp3')
    };
    
    this.isEnabled = true;
    this.volume = 0.7;
    this.useBeepFallback = false;
    
    // Initialize sound settings
    this.initializeSounds();
    
    // Load settings from localStorage
    this.loadSettings();
    
    // Check if sound files are available
    this.checkSoundFiles();
  }

  // Check if sound files are available
  checkSoundFiles() {
    let filesAvailable = 0;
    const totalFiles = Object.keys(this.sounds).length;
    
    Object.keys(this.sounds).forEach(soundType => {
      const audio = this.sounds[soundType];
      
      audio.addEventListener('canplaythrough', () => {
        filesAvailable++;
        if (filesAvailable === totalFiles) {
          console.log('✅ All sound files loaded successfully');
          this.useBeepFallback = false;
        }
      });
      
      audio.addEventListener('error', () => {
        console.warn(`❌ Sound file not found: ${soundType}.mp3`);
        this.useBeepFallback = true;
      });
    });
    
    // Set a timeout to enable fallback if files don't load
    setTimeout(() => {
      if (filesAvailable === 0) {
        console.warn('⚠️ No sound files found, using beep fallback');
        this.useBeepFallback = true;
      }
    }, 2000);
  }

  // Initialize sound objects with default settings
  initializeSounds() {
    Object.keys(this.sounds).forEach(soundType => {
      const audio = this.sounds[soundType];
      audio.volume = this.volume;
      audio.preload = 'auto';
    });
  }

  // Load sound settings from localStorage
  loadSettings() {
    const savedSettings = localStorage.getItem('soundSettings');
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        this.isEnabled = settings.isEnabled !== undefined ? settings.isEnabled : true;
        this.volume = settings.volume !== undefined ? settings.volume : 0.7;
        this.updateVolume();
      } catch (error) {
        console.error('Error loading sound settings:', error);
      }
    }
  }

  // Save sound settings to localStorage
  saveSettings() {
    const settings = {
      isEnabled: this.isEnabled,
      volume: this.volume
    };
    localStorage.setItem('soundSettings', JSON.stringify(settings));
  }

  // Update volume for all sounds
  updateVolume() {
    Object.keys(this.sounds).forEach(soundType => {
      this.sounds[soundType].volume = this.volume;
    });
  }

  // Play success sound
  playSuccess() {
    this.playSound('success');
  }

  // Play error sound
  playError() {
    this.playSound('error');
  }

  // Play warning sound
  playWarning() {
    this.playSound('warning');
  }

  // Create beep sound using Web Audio API
  createBeepSound(frequency, duration, type = 'sine') {
    if (!window.AudioContext && !window.webkitAudioContext) {
      console.warn('Web Audio API not supported');
      return;
    }

    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();

    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);

    oscillator.frequency.value = frequency;
    oscillator.type = type;
    gainNode.gain.setValueAtTime(this.volume * 0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + duration);
  }

  // Play beep fallback sounds
  playBeepSound(soundType) {
    switch (soundType) {
      case 'success':
        // High-pitched pleasant beep
        this.createBeepSound(800, 0.2);
        setTimeout(() => this.createBeepSound(1000, 0.2), 100);
        break;
      case 'error':
        // Low-pitched warning beep
        this.createBeepSound(300, 0.5);
        break;
      case 'warning':
        // Medium-pitched attention beep
        this.createBeepSound(600, 0.3);
        setTimeout(() => this.createBeepSound(600, 0.3), 200);
        break;
      default:
        this.createBeepSound(500, 0.2);
    }
  }

  // Generic play sound method
  playSound(soundType) {
    if (!this.isEnabled) return;
    
    // Use beep fallback if MP3 files are not available
    if (this.useBeepFallback) {
      this.playBeepSound(soundType);
      return;
    }
    
    const audio = this.sounds[soundType];
    if (audio) {
      try {
        // Reset audio to beginning and play
        audio.currentTime = 0;
        const playPromise = audio.play();
        
        // Handle play promise for modern browsers
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.warn(`Could not play ${soundType} sound:`, error.message);
            // Fallback to beep sound if MP3 fails
            this.playBeepSound(soundType);
          });
        }
      } catch (error) {
        console.warn(`Error playing ${soundType} sound:`, error.message);
        // Fallback to beep sound if MP3 fails
        this.playBeepSound(soundType);
      }
    } else {
      // Fallback to beep sound if audio object doesn't exist
      this.playBeepSound(soundType);
    }
  }

  // Enable/disable sounds
  setEnabled(enabled) {
    this.isEnabled = enabled;
    this.saveSettings();
  }

  // Set volume (0.0 to 1.0)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    this.updateVolume();
    this.saveSettings();
  }

  // Get current settings
  getSettings() {
    return {
      isEnabled: this.isEnabled,
      volume: this.volume
    };
  }

  // Test sound playback
  testSound(soundType) {
    console.log(`Testing ${soundType} sound...`);
    this.playSound(soundType);
  }

  // Play sound based on toast type (for react-hot-toast integration)
  playForToastType(toastType) {
    switch (toastType) {
      case 'success':
        this.playSuccess();
        break;
      case 'error':
        this.playError();
        break;
      case 'warning':
        this.playWarning();
        break;
      default:
        // Default to success for other types
        this.playSuccess();
        break;
    }
  }

  // Play sound for different ERP actions
  playForAction(action, success = true) {
    const actionSounds = {
      // Login/Logout actions
      login: success ? 'success' : 'error',
      logout: 'success',
      
      // User management actions
      createUser: success ? 'success' : 'error',
      updateUser: success ? 'success' : 'error',
      deleteUser: success ? 'success' : 'error',
      
      // Service management actions
      createService: success ? 'success' : 'error',
      updateService: success ? 'success' : 'error',
      deleteService: success ? 'success' : 'error',
      
      // Sales actions
      addSale: success ? 'success' : 'error',
      updateSale: success ? 'success' : 'error',
      deleteSale: success ? 'success' : 'error',
      
      // System actions
      save: success ? 'success' : 'error',
      delete: success ? 'success' : 'error',
      update: success ? 'success' : 'error',
      
      // Session management
      sessionWarning: 'warning',
      sessionExpired: 'error',
      
      // Form validation
      validationError: 'error',
      formSubmit: success ? 'success' : 'error'
    };

    const soundType = actionSounds[action];
    if (soundType) {
      this.playSound(soundType);
    } else {
      // Default fallback
      this.playSound(success ? 'success' : 'error');
    }
  }
}

// Create singleton instance
const soundManager = new SoundManager();

// Export both the class and the instance
export { SoundManager };
export default soundManager;
